import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Wallet, Clock, Package, Smartphone, CheckCircle2, Send, Loader2, Upload, IdCard, Bike, FileBadge, ShieldCheck } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { normalisePhoneGuinea, isValidPhoneGuinea } from "@/utils/phone";

const ZONES = [
  "Kaloum", "Dixinn", "Ratoma", "Matam", "Matoto",
  "Kipé", "Lambanyi", "Nongo", "Sonfonia", "Cosa", "Taouyah",
  "Hamdallaye", "Bambeto", "Enta",
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MOTORIZED = ["moto", "scooter"];

const EMPTY_FORM = {
  full_name: "", phone: "", email: "", zone: "", vehicle: "moto",
  birth_date: "", id_doc_type: "cni", id_doc_front_url: "", id_doc_back_url: "",
  is_vehicle_owner: true, vehicle_owner_name: "", vehicle_registration_url: "", vehicle_authorization_url: "",
  has_license: true, license_front_url: "", license_back_url: "",
  insurance_url: "",
};

export default function BecomeDriver() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const motorized = MOTORIZED.includes(form.vehicle);

  const computeAge = (birth) => {
    const b = new Date(birth);
    if (isNaN(b.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - b.getFullYear();
    if (now.getMonth() < b.getMonth() || (now.getMonth() === b.getMonth() && now.getDate() < b.getDate())) age -= 1;
    return age;
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = t("becomeDriver.required");
    if (!form.phone.trim()) e.phone = t("becomeDriver.required");
    else if (!isValidPhoneGuinea(form.phone)) e.phone = t("becomeDriver.invalidPhone");
    if (!form.email.trim()) e.email = t("becomeDriver.required");
    else if (!EMAIL_RE.test(form.email.trim())) e.email = t("becomeDriver.invalidEmail");
    if (!form.zone) e.zone = t("becomeDriver.required");
    // Identité
    if (!form.birth_date) e.birth_date = t("becomeDriver.required");
    else {
      const age = computeAge(form.birth_date);
      if (age === null || age > 90) e.birth_date = t("becomeDriver.invalidBirthDate");
      else if (age < 18) e.birth_date = t("becomeDriver.under18");
    }
    if (!form.id_doc_front_url) e.id_doc_front_url = t("becomeDriver.docRequired");
    if (form.id_doc_type !== "passeport" && !form.id_doc_back_url) e.id_doc_back_url = t("becomeDriver.docRequired");
    // Véhicule / permis (motorisé uniquement)
    if (motorized) {
      if (form.is_vehicle_owner) {
        if (!form.vehicle_registration_url) e.vehicle_registration_url = t("becomeDriver.docRequired");
      } else {
        if (!form.vehicle_owner_name.trim()) e.vehicle_owner_name = t("becomeDriver.required");
        if (!form.vehicle_authorization_url) e.vehicle_authorization_url = t("becomeDriver.docRequired");
      }
      if (form.has_license) {
        if (!form.license_front_url) e.license_front_url = t("becomeDriver.docRequired");
        if (!form.license_back_url) e.license_back_url = t("becomeDriver.docRequired");
      }
    }
    setErrors(e);
    if (Object.keys(e).length > 0) toast.error(t("becomeDriver.fixErrors"));
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      await apiClient.post("/candidatures/livreurs", {
        full_name: form.full_name.trim(),
        phone: "+" + normalisePhoneGuinea(form.phone),
        email: form.email.trim().toLowerCase(),
        zone: form.zone,
        vehicle: form.vehicle,
        birth_date: form.birth_date,
        id_doc_type: form.id_doc_type,
        id_doc_front_url: form.id_doc_front_url,
        id_doc_back_url: form.id_doc_back_url || null,
        is_vehicle_owner: motorized ? form.is_vehicle_owner : true,
        vehicle_owner_name: motorized && !form.is_vehicle_owner ? form.vehicle_owner_name.trim() : null,
        vehicle_registration_url: motorized && form.is_vehicle_owner ? form.vehicle_registration_url : null,
        vehicle_authorization_url: motorized && !form.is_vehicle_owner ? form.vehicle_authorization_url : null,
        has_license: motorized ? form.has_license : true,
        license_front_url: motorized && form.has_license ? form.license_front_url : null,
        license_back_url: motorized && form.has_license ? form.license_back_url : null,
        insurance_url: form.insurance_url || null,
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setForm({ ...EMPTY_FORM });
    setErrors({});
    setSent(false);
  };

  if (sent) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="w-16 h-16 rounded-full bg-[#E7F6EE] text-brand-green grid place-items-center mx-auto mb-5">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <div className="text-xs uppercase tracking-[0.25em] text-brand-green mb-2">{t("becomeDriver.kicker")}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold mb-3">{t("becomeDriver.successTitle")}</h1>
        <p className="text-mute text-lg mb-6">{t("becomeDriver.successMsg")}</p>
        <button
          onClick={reset}
          data-testid="driver-app-reset"
          className="rounded-full bg-white border border-sand hover:border-brand-red hover:text-brand-red px-5 py-3 font-semibold"
        >
          {t("becomeDriver.anotherOne")}
        </button>
      </div>
    );
  }

  const args = [
    { icon: <Wallet className="w-5 h-5" />, title: t("becomeDriver.arg1Title"), desc: t("becomeDriver.arg1Desc") },
    { icon: <Clock className="w-5 h-5" />, title: t("becomeDriver.arg2Title"), desc: t("becomeDriver.arg2Desc") },
    { icon: <Package className="w-5 h-5" />, title: t("becomeDriver.arg3Title"), desc: t("becomeDriver.arg3Desc") },
    { icon: <Smartphone className="w-5 h-5" />, title: t("becomeDriver.arg4Title"), desc: t("becomeDriver.arg4Desc") },
  ];

  const inputCls = "w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-xs uppercase tracking-[0.25em] text-brand-red mb-2">{t("becomeDriver.kicker")}</div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.1]">
        {t("becomeDriver.title")}
      </h1>
      <p className="mt-4 text-lg text-mute max-w-2xl">{t("becomeDriver.subtitle")}</p>

      <section className="mt-10">
        <h2 className="font-display text-xl sm:text-2xl font-semibold mb-4">{t("becomeDriver.argsTitle")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {args.map((a, i) => (
            <div key={i} className="bg-white border border-sand rounded-2xl p-5 flex gap-4 items-start" data-testid={`driver-arg-${i + 1}`}>
              <div className="w-10 h-10 rounded-full bg-[#FFF5F6] text-brand-red grid place-items-center shrink-0">{a.icon}</div>
              <div>
                <h3 className="font-display text-lg font-semibold mb-1">{a.title}</h3>
                <p className="text-sm text-mute leading-relaxed">{a.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 bg-elevated rounded-3xl p-6 sm:p-8">
        <h2 className="font-display text-xl sm:text-2xl font-semibold mb-1">{t("becomeDriver.formTitle")}</h2>
        <p className="text-sm text-mute mb-5">{t("becomeDriver.formIntro")}</p>

        <form onSubmit={submit} data-testid="driver-app-form" className="space-y-8">
          {/* ============ 1. Informations personnelles ============ */}
          <FormSection icon={<IdCard className="w-4 h-4" />} title={t("becomeDriver.sectionInfo")}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t("becomeDriver.fullName")} error={errors.full_name}>
                <input value={form.full_name} onChange={(e) => set("full_name", e.target.value)}
                  placeholder={t("becomeDriver.fullNamePh")} data-testid="driver-app-name" className={inputCls} />
              </Field>
              <Field label={t("becomeDriver.phone")} error={errors.phone}>
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                  placeholder={t("becomeDriver.phonePh")} inputMode="tel" data-testid="driver-app-phone" className={inputCls} />
              </Field>
              <Field label={t("becomeDriver.email")} error={errors.email}>
                <input value={form.email} onChange={(e) => set("email", e.target.value)}
                  placeholder={t("becomeDriver.emailPh")} inputMode="email" type="email" data-testid="driver-app-email" className={inputCls} />
              </Field>
              <Field label={t("becomeDriver.zone")} error={errors.zone}>
                <select value={form.zone} onChange={(e) => set("zone", e.target.value)} data-testid="driver-app-zone" className={inputCls}>
                  <option value="">{t("becomeDriver.zonePh")}</option>
                  {ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
                </select>
              </Field>
              <Field label={t("becomeDriver.birthDate")} error={errors.birth_date}>
                <input type="date" value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)}
                  max={new Date().toISOString().slice(0, 10)} data-testid="driver-app-birthdate" className={inputCls} />
                <span className="text-[11px] text-mute mt-1 block">{t("becomeDriver.birthDateHint")}</span>
              </Field>
            </div>
          </FormSection>

          {/* ============ 2. Pièce d'identité ============ */}
          <FormSection icon={<IdCard className="w-4 h-4" />} title={t("becomeDriver.sectionId")}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t("becomeDriver.idType")}>
                <select value={form.id_doc_type} onChange={(e) => set("id_doc_type", e.target.value)}
                  data-testid="driver-app-idtype" className={inputCls}>
                  <option value="cni">{t("becomeDriver.idCni")}</option>
                  <option value="passeport">{t("becomeDriver.idPasseport")}</option>
                  <option value="carte_sejour">{t("becomeDriver.idSejour")}</option>
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3 md:col-span-1">
                <DocUpload label={t("becomeDriver.idFront")} value={form.id_doc_front_url}
                  onUploaded={(url) => set("id_doc_front_url", url)} error={errors.id_doc_front_url} testid="driver-app-id-front" />
                {form.id_doc_type !== "passeport" && (
                  <DocUpload label={t("becomeDriver.idBack")} value={form.id_doc_back_url}
                    onUploaded={(url) => set("id_doc_back_url", url)} error={errors.id_doc_back_url} testid="driver-app-id-back" />
                )}
              </div>
            </div>
          </FormSection>

          {/* ============ 3. Véhicule ============ */}
          <FormSection icon={<Bike className="w-4 h-4" />} title={t("becomeDriver.sectionVehicle")}>
            <Field label={t("becomeDriver.vehicle")}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { v: "moto", label: t("becomeDriver.vehicleMoto") },
                  { v: "scooter", label: t("becomeDriver.vehicleScooter") },
                  { v: "velo", label: t("becomeDriver.vehicleVelo") },
                  { v: "a_pied", label: t("becomeDriver.vehicleApied") },
                ].map((o) => (
                  <button key={o.v} type="button" onClick={() => set("vehicle", o.v)}
                    data-testid={`driver-app-vehicle-${o.v}`}
                    className={`rounded-xl px-3 py-3 text-sm font-semibold border transition-colors ${
                      form.vehicle === o.v ? "bg-ink text-white border-ink" : "bg-white border-sand hover:border-brand-red"
                    }`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </Field>

            {motorized && (
              <div className="mt-4 space-y-4">
                <label className="flex items-start gap-3 cursor-pointer bg-white border border-sand rounded-xl px-4 py-3">
                  <input type="checkbox" checked={!form.is_vehicle_owner}
                    onChange={(e) => set("is_vehicle_owner", !e.target.checked)}
                    data-testid="driver-app-not-owner" className="mt-1 accent-brand-red w-4 h-4" />
                  <span className="text-sm">
                    <span className="font-semibold block">{t("becomeDriver.notOwner")}</span>
                    <span className="text-mute">{t("becomeDriver.notOwnerHint")}</span>
                  </span>
                </label>

                {form.is_vehicle_owner ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <DocUpload label={t("becomeDriver.carteGrise")} value={form.vehicle_registration_url}
                      onUploaded={(url) => set("vehicle_registration_url", url)} error={errors.vehicle_registration_url} testid="driver-app-carte-grise" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label={t("becomeDriver.ownerName")} error={errors.vehicle_owner_name}>
                      <input value={form.vehicle_owner_name} onChange={(e) => set("vehicle_owner_name", e.target.value)}
                        placeholder={t("becomeDriver.ownerNamePh")} data-testid="driver-app-owner-name" className={inputCls} />
                    </Field>
                    <DocUpload label={t("becomeDriver.authorization")} value={form.vehicle_authorization_url}
                      onUploaded={(url) => set("vehicle_authorization_url", url)} error={errors.vehicle_authorization_url} testid="driver-app-authorization" />
                  </div>
                )}
              </div>
            )}
          </FormSection>

          {/* ============ 4. Permis de conduire (motorisé) ============ */}
          {motorized && (
            <FormSection icon={<FileBadge className="w-4 h-4" />} title={t("becomeDriver.sectionLicense")}>
              <label className="flex items-start gap-3 cursor-pointer bg-white border border-sand rounded-xl px-4 py-3 mb-4">
                <input type="checkbox" checked={!form.has_license}
                  onChange={(e) => set("has_license", !e.target.checked)}
                  data-testid="driver-app-no-license" className="mt-1 accent-brand-red w-4 h-4" />
                <span className="text-sm">
                  <span className="font-semibold block">{t("becomeDriver.noLicense")}</span>
                  <span className="text-mute">{t("becomeDriver.noLicenseHint")}</span>
                </span>
              </label>
              {form.has_license && (
                <div className="grid grid-cols-2 gap-3 max-w-md">
                  <DocUpload label={t("becomeDriver.licenseFront")} value={form.license_front_url}
                    onUploaded={(url) => set("license_front_url", url)} error={errors.license_front_url} testid="driver-app-license-front" />
                  <DocUpload label={t("becomeDriver.licenseBack")} value={form.license_back_url}
                    onUploaded={(url) => set("license_back_url", url)} error={errors.license_back_url} testid="driver-app-license-back" />
                </div>
              )}
            </FormSection>
          )}

          {/* ============ 5. Assurance (optionnelle) ============ */}
          <FormSection icon={<ShieldCheck className="w-4 h-4" />} title={t("becomeDriver.sectionInsurance")}>
            <p className="text-sm text-mute mb-3">{t("becomeDriver.insuranceHint")}</p>
            <div className="max-w-[220px]">
              <DocUpload label={t("becomeDriver.insurance")} value={form.insurance_url}
                onUploaded={(url) => set("insurance_url", url)} testid="driver-app-insurance" />
            </div>
          </FormSection>

          <div>
            <button type="submit" disabled={busy} data-testid="driver-app-submit"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-brand-red hover:bg-[#C81F2D] disabled:opacity-60 text-white px-6 py-3 font-semibold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {busy ? t("becomeDriver.submitting") : t("becomeDriver.submit")}
            </button>
          </div>
        </form>
        <p className="mt-6 text-sm text-mute text-center" data-testid="become-driver-login-hint">
          {t("becomeDriver.alreadyDriver")} <Link to="/espace-partenaires" data-testid="become-driver-login-link" className="text-brand-red font-semibold">{t("becomeDriver.partnerLoginLink")}</Link>
        </p>
      </section>
    </div>
  );
}

function FormSection({ icon, title, children }) {
  return (
    <div className="bg-[#FAF7F2] border border-sand rounded-2xl p-4 sm:p-5">
      <h3 className="flex items-center gap-2 font-display text-base font-semibold mb-4">
        <span className="w-7 h-7 rounded-full bg-[#FFF5F6] text-brand-red grid place-items-center">{icon}</span>
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-mute block mb-2">{label}</span>
      {children}
      {error && <span className="text-xs text-brand-red mt-1 block">{error}</span>}
    </label>
  );
}

/** Upload d'un document (photo) vers /api/candidatures/uploads — public, sans compte. */
function DocUpload({ label, value, onUploaded, error, testid }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await apiClient.post("/candidatures/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data.url);
      toast.success(t("becomeDriver.docUploaded"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <span className="text-xs uppercase tracking-widest text-mute block mb-2">{label}</span>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf,.pdf" onChange={onFile} className="hidden" data-testid={`${testid}-input`} />
      <button type="button" onClick={pick} disabled={uploading} data-testid={testid}
        className={`w-full rounded-xl border-2 border-dashed px-3 py-4 text-sm font-medium flex flex-col items-center gap-1.5 transition-colors ${
          value ? "border-brand-green bg-[#E7F6EE] text-brand-green"
            : error ? "border-brand-red bg-[#FFF5F6] text-brand-red"
            : "border-sand bg-white text-mute hover:border-brand-red hover:text-brand-red"
        }`}>
        {uploading ? <Loader2 className="w-5 h-5 animate-spin" />
          : value ? <CheckCircle2 className="w-5 h-5" />
          : <Upload className="w-5 h-5" />}
        {uploading ? t("becomeDriver.docUploading") : value ? t("becomeDriver.docReplace") : t("becomeDriver.docUpload")}
      </button>
      <span className="text-xs text-mute mt-1 block">{t("becomeDriver.docFormats")}</span>
      {error && <span className="text-xs text-brand-red mt-1 block">{error}</span>}
    </div>
  );
}
